import { 
  Leaf, 
  Recycle, 
  Droplets, 
  Wind, 
  Sun, 
  Target, 
  TrendingUp,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const Sustainability = () => {
  const initiatives = [
    {
      icon: <Recycle className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Circular Economy",
      description: "Transforming waste into valuable resources through innovative recycling processes",
      impact: "85% waste diversion from landfills"
    },
    {
      icon: <Droplets className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Water Conservation",
      description: "Advanced water treatment and recycling systems in our operations",
      impact: "40% reduction in water usage"
    },
    {
      icon: <Wind className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Emission Reduction",
      description: "State-of-the-art emission control systems and carbon footprint reduction",
      impact: "60% reduction in emissions"
    },
    {
      icon: <Sun className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Renewable Energy",
      description: "Solar-powered facilities and energy recovery from waste processes",
      impact: "50% renewable energy usage"
    }
  ];

  const practices = [
    {
      category: "Waste Reduction",
      items: [
        "Source reduction strategies",
        "Waste minimization consulting",
        "Packaging optimization",
        "Process efficiency improvements"
      ]
    },
    {
      category: "Resource Recovery",
      items: [
        "Material recovery and sorting",
        "Energy recovery from waste",
        "Nutrient recovery systems",
        "Metal and plastic reclamation"
      ]
    },
    {
      category: "Environmental Protection",
      items: [
        "Groundwater protection",
        "Air quality monitoring",
        "Soil contamination prevention",
        "Biodiversity conservation"
      ]
    },
    {
      category: "Community Impact",
      items: [
        "Local employment creation",
        "Community education programs",
        "Health and safety training",
        "Environmental awareness campaigns"
      ]
    }
  ];

  const goals = [
    {
      year: "2024",
      title: "Carbon Neutral Operations",
      description: "Achieve carbon neutrality across all operational facilities",
      status: "In Progress"
    },
    {
      year: "2025",
      title: "90% Waste Diversion",
      description: "Divert 90% of all processed waste from landfills",
      status: "Target"
    },
    {
      year: "2026",
      title: "Zero Liquid Discharge",
      description: "Implement zero liquid discharge systems",
      status: "Planned"
    },
    {
      year: "2027",
      title: "100% Renewable Energy",
      description: "Power all operations with renewable energy sources",
      status: "Vision"
    }
  ];

  return (
    <div className="w-full overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 md:py-20 xl:py-24 bg-gradient-to-br from-[#295cab] to-[#1e4a8a] text-white w-full">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 md:px-8 xl:px-12 text-center w-full">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-full mb-3 sm:mb-6">
            <Leaf className="w-8 h-8 sm:w-10 sm:h-10" />
          </div>
          <h1 className="text-2xl sm:text-4xl md:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6">Sustainability</h1>
          <p className="text-base sm:text-lg md:text-xl xl:text-2xl max-w-3xl mx-auto leading-relaxed px-2 sm:px-4">
            Building a cleaner future through responsible waste management and environmental stewardship
          </p>
        </div>
      </section>

      {/* Environmental Commitment */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">Our Environmental Commitment</h2>
              <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">
                At BioHaz Waste Solutions, sustainability isn't just a buzzword—it's the foundation of everything we do. 
                We believe that responsible waste management is crucial for protecting our planet and ensuring a healthy 
                future for generations to come.
              </p>
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                {[
                  "Minimize environmental impact through innovative technologies",
                  "Maximize resource recovery and recycling opportunities",
                  "Promote circular economy principles in all operations",
                  "Continuously improve our environmental performance",
                  "Educate communities about sustainable practices"
                ].map((commitment, index) => (
                  <div key={index} className="flex items-start gap-2 sm:gap-3">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#295cab] mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 text-xs sm:text-sm md:text-base">{commitment}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative order-1 lg:order-2">
              <img 
                src="/work.jpg" 
                alt="Environmental Protection" 
                className="rounded-xl shadow-2xl w-full object-cover max-h-72 sm:max-h-96"
              />
              <div className="absolute -bottom-3 -left-3 sm:-bottom-6 sm:-left-6 bg-[#295cab] text-white p-3 sm:p-6 rounded-lg">
                <div className="text-center">
                  <div className="text-lg sm:text-xl md:text-2xl font-bold">85%</div>
                  <div className="text-xs sm:text-sm">Waste Diverted</div>
                  <div className="text-xs sm:text-sm">from Landfills</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sustainability Initiatives */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-gray-900 mb-4">Sustainability Initiatives</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Our comprehensive approach to environmental responsibility drives measurable impact
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {initiatives.map((initiative, index) => (
              <div key={index} className="bg-white p-4 sm:p-6 md:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="text-[#295cab] mb-4">{initiative.icon}</div>
                <h3 className="text-xs sm:text-sm md:text-lg font-semibold text-gray-900 mb-2">{initiative.title}</h3>
                <p className="text-gray-600 mb-3 text-xs sm:text-sm md:text-base">{initiative.description}</p>
                <div className="bg-blue-50 px-2 py-1 sm:px-3 sm:py-2 rounded-lg">
                  <span className="text-[#1e4a8a] font-semibold text-xs sm:text-sm">{initiative.impact}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainable Practices */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-gray-900 mb-4">Sustainable Practices</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Comprehensive strategies that minimize environmental impact while maximizing resource recovery
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {practices.map((practice, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 p-3 sm:p-4 md:p-6 rounded-xl">
                <h3 className="text-xs sm:text-sm md:text-base font-semibold text-gray-900 mb-2 sm:mb-4">{practice.category}</h3>
                <ul className="space-y-1 sm:space-y-2">
                  {practice.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-1 sm:gap-2">
                      <div className="w-2 h-2 bg-[#295cab] rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-600 text-xs sm:text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability Goals */}
      <section className="py-8 sm:py-12 md:py-16 xl:py-20 bg-gradient-to-br from-[#295cab] to-[#1e4a8a] text-white w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold mb-4">Our Sustainability Goals</h2>
            <p className="text-xs sm:text-sm md:text-base xl:text-lg max-w-2xl mx-auto px-2 sm:px-4">
              Ambitious targets that drive continuous improvement in our environmental performance
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {goals.map((goal, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm p-3 sm:p-4 md:p-6 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300">
                <div className="flex items-center justify-between mb-2 sm:mb-4">
                  <span className="text-base sm:text-xl md:text-2xl font-bold text-blue-300">{goal.year}</span>
                  <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold 
                    ${goal.status === 'In Progress' ? 'bg-yellow-500 text-yellow-900' :
                    goal.status === 'Target' ? 'bg-blue-500 text-blue-900' :
                    goal.status === 'Planned' ? 'bg-purple-500 text-purple-900' :
                    'bg-gray-500 text-gray-900'
                  }`}>
                    {goal.status}
                  </span>
                </div>
                <h3 className="text-xs sm:text-sm md:text-base font-semibold mb-2">{goal.title}</h3>
                <p className="text-blue-100 text-xs sm:text-sm">{goal.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Circular Economy Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <img 
                src="/garbage.png" 
                alt="Circular Economy" 
                className="rounded-xl shadow-2xl w-full object-cover max-h-72 sm:max-h-96"
              />
              <div className="absolute -top-3 -right-3 sm:-top-6 sm:-right-6 bg-[#295cab] text-white p-3 sm:p-6 rounded-lg">
                <div className="flex items-center gap-1 sm:gap-2">
                  <Recycle className="w-6 h-6 sm:w-8 sm:h-8" />
                  <div>
                    <div className="font-semibold text-xs sm:text-sm md:text-base">Circular</div>
                    <div className="text-xs sm:text-sm">Economy</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">Embracing the Circular Economy</h2>
              <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">
                We're pioneering the transition from a linear "take-make-dispose" model to a circular economy 
                where waste becomes a resource. Our innovative processes transform hazardous materials into 
                valuable inputs for new products and energy generation.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 md:gap-6">
                <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
                  <Target className="w-5 h-5 sm:w-6 sm:h-6 text-[#295cab] mb-2" />
                  <h4 className="font-semibold text-gray-900 mb-1 text-xs sm:text-sm md:text-base">Resource Recovery</h4>
                  <p className="text-gray-600 text-xs sm:text-sm">Converting waste into raw materials for manufacturing</p>
                </div>
                <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
                  <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 mb-2" />
                  <h4 className="font-semibold text-gray-900 mb-1 text-xs sm:text-sm md:text-base">Energy Generation</h4>
                  <p className="text-gray-600 text-xs sm:text-sm">Producing clean energy from waste materials</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership CTA */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 md:px-8 xl:px-12 text-center w-full">
          <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">Partner with Us for a Sustainable Future</h2>
          <p className="text-xs sm:text-sm md:text-base xl:text-lg text-gray-600 mb-4 sm:mb-8 max-w-2xl mx-auto px-2 sm:px-4">
            Join us in building a cleaner, more sustainable Uganda. Together, we can make a lasting impact on our environment.
          </p>
          <a 
            href="/contact" 
            className="inline-flex items-center gap-1 sm:gap-2 bg-[#295cab] text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 rounded-lg font-semibold hover:bg-[#1e4a8a] transition-all transform hover:scale-105 text-xs sm:text-sm md:text-base"
          >
            <span>Start Your Sustainability Journey</span>
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Sustainability;